// @flow
import React, { forwardRef, type Node } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';

import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { useTracking } from 'Tracking/tracking-api/useTracking';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './Tag.themes';

export type TagProps = {
  active?: boolean,
  badge?: Node | string,
  borderColor?: Colors,
  className?: string,
  closeLabel?: string,
  color?: Colors,
  dataTestId?: string,
  iconLeft?: IconName,
  iconRight?: IconName,
  label?: Node,
  onClick?: (SyntheticEvent<>) => *,
  onClose?: (e?: Event) => *,
  tabIndex?: number,
  theme?: string,
  trackingData?: ?TrackingData,
};

export const Tag = forwardRef<TagProps, *>((props: TagProps, ref) => {
  const {
    active,
    badge,
    borderColor,
    className,
    closeLabel,
    color = COLORS.BLUE,
    dataTestId = 'Tag',
    iconLeft,
    iconRight,
    label,
    onClick,
    onClose,
    tabIndex = 0,
    theme = 'default',
    trackingData = {},
    ...rest
  } = props;

  const track = trackingData && onClick ? useTracking(trackingData) : noop;

  if (!label) {
    return null;
  }

  const TagName = onClick ? 'button' : 'div';

  const {
    thRoot,
    thBody,
    thBadge,
    thBadgeStyle,
    thBadgeLabel,
    thIconLeft,
    thIconRight,
    thIconSize,
    thLabel,
    thClose,
    thCloseSize,
  } = themes[theme](props);

  return (
    <div className={classnames(className, `text-${color} border-${borderColor || color}`, thRoot)}>
      <TagName
        {...rest}
        className={thBody}
        data-testid={dataTestId}
        onClick={e => {
          onClick && onClick(e);
          track({});
        }}
        ref={ref}
        title={label}
        {...(onClick ? { tabIndex, type: 'button' } : null)}
      >
        {!!badge && (
          <div key="badge" className={thBadge} style={thBadgeStyle} data-testid="Tag-badge">
            {typeof badge === 'string' ? <div className={thBadgeLabel}>{badge}</div> : badge}
          </div>
        )}
        {!!iconLeft && (
          <Icon key="icon-left" className={thIconLeft} name={iconLeft} width={thIconSize} />
        )}
        <div key="label" className={thLabel}>
          {label}
        </div>
        {!!iconRight && (
          <Icon key="icon-right" className={thIconRight} name={iconRight} width={thIconSize} />
        )}
      </TagName>
      {!!onClose && (
        <button className={thClose} onClick={onClose} tabIndex={tabIndex} type="button">
          {!!closeLabel && <span className="a11y">{closeLabel}</span>}
          <Icon name={ICONS.CROSS} width={thCloseSize} />
        </button>
      )}
    </div>
  );
});
